import { BoldUploadService } from './bold-upload-service';
import { PersonType, MimeTypeIcons } from '@bold/file-upload-service';
import { Injectable } from '@angular/core';
import { BcConfigService } from 'common/config/provider';
import { LogoutSubscriber } from '../subscribers/logout-subscriber.service';
import { BcApiService } from 'common/api/api.service';
import { UserContextService } from 'common/user-context/user-context.service';
import { FileUploadResponseModel } from 'common/api/models/file-upload-response.model';
import { HttpHeaders, HttpRequest, HttpResponse, HttpClient, HttpParams } from '@angular/common/http';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';

@Injectable()
export class FileSharingService {
  public uploadServiceMap;
  public fileMap;

  constructor(private _bcConfig: BcConfigService,
              private _bcApi: BcApiService,
              private _userContextService: UserContextService,
              private _logoutSubscriber: LogoutSubscriber,
              private _httpClient: HttpClient) {
    this.uploadServiceMap = {};
    this.fileMap = {};

    this._logoutSubscriber.subscribe(() => this._resetState());
  }

  public initiateUpload(file, chatId: string, workItemType: WorkItemTypes) {
    const commonService: BoldUploadService = this._getServiceInstanceForUpload(file, chatId, workItemType);

    this._addFile(file, chatId);

    return commonService.initiateUpload()
      .then(() => commonService.uploadFile())
      .then(null, null, (e) => {
        let done = e.position || e.loaded;
        let total = e.totalSize || e.total;

        //Note: It's possible that a last progress event arrives after the abort request was sent
        if (this.fileMap[chatId]) {
          this.fileMap[chatId].progress = Math.round(done / total * 100);
        }
      });
  }

  public abortUpload(chatId: string): void {
    this._getServiceByChatId(chatId).abortUpload();
    this.clearErrorMessage(chatId);

    this.fileMap[chatId] = null;
    this.uploadServiceMap[chatId] = null;
  }

  public sendFile(chatId: string) {
    return this._getServiceByChatId(chatId).sendFile()
      .then(() => {
        this.fileMap[chatId] = null;
        this.uploadServiceMap[chatId] = null;
      });
  }

  public hasErrorMessage(chatId: string): boolean {
    if (!this._getServiceByChatId(chatId)) {
      return false;
    }

    return this._getServiceByChatId(chatId).hasErrorMessage;
  }

  public clearErrorMessage(chatId: string): void {
    this._getServiceByChatId(chatId).hasErrorMessage = false;
  }

  public isFileUploaded(chatId: string): boolean {
    return this._getServiceByChatId(chatId).isFileUploaded();
  }

  public isSendingInProgress(chatId: string): boolean {
    return this._getServiceByChatId(chatId).isSendingInProgress();
  }

  private _resetState(): void {
    this.fileMap = {};
    this.uploadServiceMap = {};
  }

  private _getIconByContentType(chatId: string): string {
    return MimeTypeIcons[this._getServiceByChatId(chatId).getFileType()];
  }

  private _getUploadingFileSize(file): string {
    if (!file) {
      return;
    }
    return ((file.size / 1024 / 1024).toFixed(2));
  }

  private _addFile(file, chatId: string): void {
    this.fileMap[chatId] = { file, fileIcon: this._getIconByContentType(chatId), fileSize: this._getUploadingFileSize(file), progress: 0 };
  }

  private _getServiceByChatId(chatId: string) {
    return this.uploadServiceMap[chatId];
  }

  private _getServiceInstanceForUpload(file, chatId: string, workItemType: WorkItemTypes) {
    let commonService: BoldUploadService = this._getServiceByChatId(chatId);
    if (!commonService) {
      let config = { fileUploadHost: this._bcConfig.getFileUploadHost(), workItemType: workItemType };
      commonService = new BoldUploadService(config, this._getApiForUploadService(workItemType), this._getUserObject());
      this.uploadServiceMap[chatId] = commonService;
    }
    commonService.queueFile(file, chatId);
    return commonService;
  }

  private _getUserObject() {
    return {
      accountId: this._userContextService.userContext.accountId,
      personId: this._userContextService.userContext.id,
      personType: PersonType.Operator
    };
  }

  private _getApiForUploadService(workItemType: WorkItemTypes) {
    let storageType = workItemType && workItemType === WorkItemTypes.ASYNC_MESSAGE ? 'ASYNC' : 'CHAT';
    return {
      get: (url, headers, excludeCsrfToken) => this._bcApi.get(url, headers, excludeCsrfToken),
      post: (url, requestBody, headers, excludeCsrfToken) => this._bcApi.post(url, requestBody, headers, excludeCsrfToken),
      put: (url, requestBody, headers, excludeCsrfToken) => this._bcApi.put(url, requestBody, headers, excludeCsrfToken),
      upload: (url: string, file: File) => {
        const requestHeaders = new HttpHeaders({
          'X-Skip-Interceptor': 'true'
        });
        const uploadFormData = new FormData();
        const requestParams = new HttpParams({ fromObject: { storageType: storageType } });
        uploadFormData.append('file', file, file.name);
        const uploadRequest = new HttpRequest('POST', url, uploadFormData, {
          headers: requestHeaders,
          params: requestParams
        });
        return this._httpClient.request<FileUploadResponseModel>(uploadRequest).toPromise()
          .then((response: HttpResponse<FileUploadResponseModel>) => response.body);
      }
    };
  }
}
